import React, { useEffect, useMemo, useState } from 'react';

import { TableEvent } from '@baemingo/table-events';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import _ from 'lodash';
import { useQuery } from 'react-apollo';
import Calendar from 'react-calendar';
import styled from 'styled-components';

import VenuePicker from 'Components/VenuePicker';
import { getEndDate, getStartDate, getToday } from 'utils/dates';

import {
  GetEventsForMonitoring,
  GetEventsForMonitoringVariables,
  GetTableDataForMonitoring,
  GetTableDataForMonitoringVariables,
} from './__queries__';
import { useSelector } from '../../Hooks';

import '../../assets/Css/calendar-styles.css';

const EVENTS_QUERY = gql`
  query GetEventsForMonitoring($timestamp: DateTime!, $venueId: ID!) {
    venue: Venue(id: $venueId) {
      id

      events(limit: 5000, sentAfter: $timestamp) {
        lastKey
        items
      }
    }
  }
`;

const COMPANY_QUERY = gql`
  query GetTableDataForMonitoring($companyId: ID!) {
    company: Company(id: $companyId) {
      id
      staffs {
        id
        name
      }
      items(filter: { deleted: false }) {
        id
        title
      }
      venues {
        id
        name

        tables {
          id
          code
        }
      }
    }
  }
`;

const ORDERS_QUERY = gql`
  query GetOrders($venueId: ID!, $endDate: DateTime!, $startDate: DateTime!) {
    allOrders(
      filter: { creationDate_gt: $startDate, creationDate_lt: $endDate, venue: { id: $venueId } }
    ) {
      id

      payment {
        id
        amount
        correctedTip
        tip
        type
        transactionId
      }

      staff {
        id
        name
      }
    }
  }
`;

const CalendarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Wrapper = styled.div`
  padding: 10px;
  padding-bottom: 300px;
`;

const Table = styled.div`
  display: table;
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
`;

const TableRow = styled.div`
  display: table-row;
  &:nth-child(even) {
    background-color: #f2f2f2;
  }
`;

const TableHeader = styled.div`
  display: table-header-group;
  background-color: #1c232c;
  color: white;
`;

const TableHeaderCell = styled.div`
  display: table-cell;
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
  font-weight: bold;
`;

const TableBody = styled.div`
  display: table-row-group;
`;

const TableCell = styled.div`
  display: table-cell;
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
`;

const StaffExtractor = () => {
  const [startDate, setStartDate] = useState(dayjs(getStartDate(getToday())).toDate());
  const [endDate, setEndDate] = useState(dayjs(getEndDate(getToday())).toDate());
  const [venueId, setVenueId] = useState<string | null>(null);
  const companyId = useSelector((state) => state.company!);

  const [staffFilter, setStaffFilter] = useState<string[] | null>(null);
  const [eventFilter, setEventFilter] = useState<string[] | null>(null);
  const [metaFilter, setMetaFilter] = useState<string[] | null>(null);

  const companyQuery = useQuery<GetTableDataForMonitoring, GetTableDataForMonitoringVariables>(
    COMPANY_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        companyId,
      },
    },
  );

  const { data, fetchMore, loading } = useQuery<
    GetEventsForMonitoring,
    GetEventsForMonitoringVariables
  >(EVENTS_QUERY, {
    skip: !companyQuery.data?.company,
    fetchPolicy: 'network-only',
    variables: {
      timestamp: startDate,
      venueId: venueId || '',
    },
  });

  const { data: ordersData, loading: ordersLoading } = useQuery(ORDERS_QUERY, {
    skip: !venueId,
    fetchPolicy: 'network-only',
    variables: {
      venueId: venueId,
      startDate,
      endDate,
    },
  });

  useEffect(() => {
    if (!data?.venue?.events) {
      return;
    }

    // If there are not more events to fetch
    const lastEvent = data.venue.events.lastKey;

    if (!lastEvent) {
      return;
    }

    // If we've reached our end date
    const isAfter = dayjs(lastEvent.sentAt).isAfter(endDate);

    if (isAfter) {
      return;
    }

    fetchMore({
      query: EVENTS_QUERY,
      variables: {
        venueId: venueId || '',
        timestamp: lastEvent.sentAt,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const cloned = _.cloneDeep(prev);
        cloned.venue.events.items = [
          ...prev.venue.events.items,
          ...fetchMoreResult?.venue.events.items,
        ];

        cloned.venue.events.lastKey = fetchMoreResult?.venue.events.lastKey;

        return cloned;
      },
    });
  }, [data, endDate, fetchMore, venueId]);

  const company = companyQuery.data?.company;

  const events = useMemo(() => {
    let tableEvents = (data?.venue?.events.items as TableEvent[]) ?? [];
    tableEvents = tableEvents
      .filter((e) => new Date(e.createdAt) < endDate)
      .filter((e) => e.type !== 'ADD_LOG')
      .filter((e) => e.type !== 'REMOVE_NOTE');

    return _.uniqBy(tableEvents, 'eventId');
  }, [data, endDate]);

  const filteredEvents = useMemo(
    () =>
      events.filter((event) => {
        // Filter by events
        if (eventFilter && eventFilter.length > 0 && !eventFilter.includes(event.type)) {
          return false;
        }

        // Filter by staff
        if (staffFilter && staffFilter.length > 0 && !staffFilter.includes(event.type)) {
          return false;
        }

        // Filter by info / meta / reason
        if (metaFilter && metaFilter.length > 0) {
          // @ts-ignore
          if (!metaFilter.includes(event.reason)) {
            return false;
          }
        }

        return true;
      }),
    [events, eventFilter, staffFilter, metaFilter],
  );

  type StaffMap = {
    totalAmount: number;
    tip: number;
    staff: string;
    chequeId: string;
  };

  const guessedStaff = useMemo((): StaffMap[] => {
    if (!company || !ordersData) {
      return [];
    }
    const guessedStaffMap: StaffMap[] = [];

    filteredEvents.forEach((event) => {
      if (event.type === 'ADD_PAYMENT' && !event.staffId) {
        const order = ordersData.allOrders.find(
          (order) => order.payment.transactionId === event.transactionId,
        );

        if (!order) {
          return;
        }

        if (!order.staff) {
          const chequePrintedEvent = filteredEvents.find(
            (e) => e.type === 'CHEQUE_PRINTED' && e.id === event.chequeId,
          );

          const createChequeEvent = filteredEvents.find(
            (e) => e.type === 'CREATE_CHEQUE' && e.id === event.chequeId,
          );

          if (chequePrintedEvent || createChequeEvent) {
            const eventToTakeStaffFrom = chequePrintedEvent || createChequeEvent;
            if (!eventToTakeStaffFrom) {
              console.log('No event to take staff from');
              return;
            }
            const staffName =
              company.staffs.find((s) => s.id === eventToTakeStaffFrom.staffId)?.name ?? '--';
            guessedStaffMap.push({
              totalAmount: (event.amount + event.tip) / 100,
              tip: event.tip / 100,
              staff: staffName,
              chequeId: event.chequeId,
            });
          }
        }
      }
    });

    const ordersWithoutTransactionId = ordersData.allOrders.filter(
      (order) =>
        !order.payment.transactionId && order.payment.type !== 'ADYEN_MANUELL' && !order.staff,
    );

    if (ordersWithoutTransactionId.length > 0) {
      ordersWithoutTransactionId.forEach((order) => {
        // Find payment event based on amount and tip
        const paymentEvent = filteredEvents.find(
          (e) =>
            e.type === 'ADD_PAYMENT' &&
            e.amount === order.payment.amount &&
            e.tip === order.payment.tip,
        );

        if (!paymentEvent) {
          // We don't have a TSR event matching the payment.
          // Add it as a POS Unregistered tip
          guessedStaffMap.push({
            totalAmount: (order.payment.amount + order.payment.tip) / 100,
            tip: order.payment.tip / 100,
            staff: 'POS Unregistered Tip',
            chequeId: `From: ${order.payment.type} - ${order.id}`,
          });
          return;
        }

        // We ensure we have a ADD_PAYMENT event for TS.
        if (paymentEvent.type !== 'ADD_PAYMENT') {
          return;
        }

        const chequePrintedEvent = filteredEvents.find(
          (e) => e.type === 'CHEQUE_PRINTED' && e.id === paymentEvent.chequeId,
        );

        if (chequePrintedEvent) {
          const staffName =
            company.staffs.find((s) => s.id === chequePrintedEvent.staffId)?.name ?? '--';
          guessedStaffMap.push({
            totalAmount: (paymentEvent.amount + paymentEvent.tip) / 100,
            tip: paymentEvent.tip / 100,
            staff: staffName,
            chequeId: `From: ${order.payment.type} - ${paymentEvent.chequeId}`,
          });
        }
      });
    }

    guessedStaffMap.sort((a, b) => a.staff.localeCompare(b.staff));

    return guessedStaffMap;
  }, [filteredEvents, company]);

  const calculateTotals = (guessedStaff) => {
    const totals = {};
    guessedStaff.forEach((staff) => {
      if (!totals[staff.staff]) {
        totals[staff.staff] = 0;
      }
      totals[staff.staff] += staff.totalAmount;
    });
    return totals;
  };

  const calculateTotalTips = (guessedStaff) => {
    const totals = {};
    guessedStaff.forEach((staff) => {
      if (!totals[staff.staff]) {
        totals[staff.staff] = 0;
      }
      totals[staff.staff] += staff.tip;
    });

    return totals;
  };

  const GuessedStaffTable = ({ guessedStaff }) => {
    const totals = calculateTotals(guessedStaff);
    const totalTips = calculateTotalTips(guessedStaff);

    // Calculate grand totals
    const grandTotalAmount = guessedStaff.reduce((sum, staff) => sum + staff.totalAmount, 0);
    const grandTotalTip = guessedStaff.reduce((sum, staff) => sum + staff.tip, 0);

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Cheque ID</TableHeaderCell>
            <TableHeaderCell>Staff</TableHeaderCell>
            <TableHeaderCell>Tip</TableHeaderCell>
            <TableHeaderCell>Total Amount</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {guessedStaff.map((staff, index, arr) => (
            <React.Fragment key={staff.chequeId}>
              <TableRow>
                <TableCell>{staff.chequeId}</TableCell>
                <TableCell>{staff.staff}</TableCell>
                <TableCell>{staff.tip}</TableCell>
                <TableCell>{staff.totalAmount}</TableCell>
              </TableRow>
              {(index === arr.length - 1 || arr[index + 1].staff !== staff.staff) && (
                <>
                  <TableRow key={`${staff.staff}-total`}>
                    <TableCell />
                    <TableCell />
                    <TableCell style={{ fontWeight: 'bold' }}>
                      {totalTips[staff.staff].toFixed(2)}
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      {totals[staff.staff].toFixed(2)}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </React.Fragment>
          ))}
          {/* Grand total row */}
          <TableRow>
            <TableCell />
            <TableCell style={{ fontWeight: 'bold' }}>Grand Total</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>{grandTotalTip.toFixed(2)}</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>{grandTotalAmount.toFixed(2)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  return (
    <Wrapper>
      <CalendarWrapper>
        <div
          style={{
            flex: '0 0 400px',
          }}>
          <h1>Staff Extractor</h1>
          <VenuePicker onIDChange={setVenueId} />
          <Calendar
            locale={'en'}
            minDate={new Date('2017-04-01')}
            maxDate={new Date()}
            onChange={(date) => {
              setStartDate(getStartDate(date).toDate());
              setEndDate(getEndDate(date).toDate());
            }}
          />
        </div>
      </CalendarWrapper>

      {loading ? <div>Loading...</div> : <GuessedStaffTable guessedStaff={guessedStaff} />}
    </Wrapper>
  );
};

export default StaffExtractor;
